import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { login, isAuthenticated } from '../services/authService';
import './Login.css';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField'; // Using TextField for better integration with InputAdornment
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button'; // For a consistent MUI look
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert'; // For displaying errors
import { useUser } from '../contexts/UserContext'; // Import useUser
import { useTenant } from '../contexts/TenantContext'; // Import useTenant
import { jwtDecode } from 'jwt-decode';
import { userManager, getOidcUserManager } from '../oidcConfig';
import Divider from '@mui/material/Divider';
import { GoogleLogin } from '@react-oauth/google';
import { useGoogleLogin } from '@react-oauth/google';
import { loginWithExternalToken } from '../services/authService';
import { PublicClientApplication } from '@azure/msal-browser';

const msalConfig = {
  auth: {
    clientId: '8c002588-dafa-4f15-9a9f-664e58fcf8f6',
    authority: 'https://login.microsoftonline.com/common',
    // redirectUri: `${window.location.origin}/oidc/common/callback`,
    redirectUri: `${window.location.origin}/sas`,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

const Index = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { fetchUserData } = useUser(); // Get fetchUserData from context
  const { fetchTenantData, fetchTenantsFromApi, tenantData } = useTenant(); // Get fetchTenantData and tenantData from context

  const loginGoogle = useGoogleLogin({
    onSuccess: tokenResponse => {
      console.log(tokenResponse);
      localStorage.setItem('access_token', tokenResponse.credential);
      window.dispatchEvent(new Event('tokenChanged'));
      navigate('/sas');
    },
    flow: 'auth-code',
  });

  useEffect(() => {
    const redirectIfAuthenticated = async () => {
      if (isAuthenticated()) {
        console.log('Index: User is authenticated. Redirecting to tenant dashboard.');

        try {
          const token = localStorage.getItem('access_token');
          const decodedToken = jwtDecode(token);
          //const tenant = decodedToken.tenants?.[0]; // Assuming the first tenant is the default

        //   if (tenant && tenant.subdomain_prefix) {
        //     navigate(`/app/${tenant.subdomain_prefix}/dashboard`);
        //   } else {
        //     console.error('Index: No tenant information found in token.');
        //   }
            navigate('/sas');

        } catch (error) {
          console.error('Index: Error decoding token.', error);
        }
      }
    };

    redirectIfAuthenticated();
  }, [navigate]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password); // Pass tenant_id to login function
      await fetchUserData();
      await fetchTenantData();

      // const tenant_id = tenantData?.subdomain_prefix; // Ensure tenant_id is defined
      // if (tenant_id) {
      //   navigate(`/app/${tenant_id}/dashboard`); // Navigate to tenant-specific dashboard
      // } else {
      //   setError(t('login.tenantIdMissing')); // Handle case where tenant_id is missing
      // }
      navigate('/sas');
    } catch (err) {
      if (err.response) {
        switch (err.response.status) {
          case 401:
            setError(t('login.loginFailedAuth'));
            break;
          case 429:
            const retryAfterHeader = err.response.headers['retry-after'] || err.response.headers['Retry-After'];
            let delaySeconds = null;
            if (retryAfterHeader) {
              if (!isNaN(parseFloat(retryAfterHeader)) && isFinite(retryAfterHeader)) {
                delaySeconds = parseInt(retryAfterHeader, 10);
              } else {
                const retryDate = new Date(retryAfterHeader);
                const nowDate = new Date();
                delaySeconds = Math.round((retryDate.getTime() - nowDate.getTime()) / 1000);
              }
            }
            if (delaySeconds && delaySeconds > 0) {
              setError(t('login.loginFailedRateLimit', { seconds: delaySeconds }));
            } else {
              setError(t('login.loginFailedRateLimitGeneric'));
            }
            break;
          default:
            setError(t('login.loginFailed'));
        }
      } else {
        setError(t('login.loginFailed'));
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // const initiateOidcFlow = (provider) => {
  //   getOidcUserManager(provider).signinRedirect();
  // };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    try {
      // Ensure the MSAL instance is initialized
      await msalInstance.initialize();

      // Proceed with the login popup
      const loginResponse = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email', 'https://graph.microsoft.com/User.Read'],
      }).then(async function (credentialResponse) {
        try {
          await loginWithExternalToken(credentialResponse.idToken, credentialResponse.accessToken);
          // window.dispatchEvent(new Event('tokenChanged'));
          await fetchUserData(); // Ensure user data is fetched after token is set


          // Fetch tenants and redirect to the first one
          const tenants = await fetchTenantsFromApi();
          if (tenants && tenants.length > 0) {
            navigate('/sas');
          }
        } catch (error) {
          console.error('Error logging in with external token:', error);
        }
      }).finally(() => { setLoading(false); });

      // console.log('Access Token:', loginResponse.accessToken);
      // localStorage.setItem('access_token', loginResponse.accessToken);
      // window.dispatchEvent(new Event('tokenChanged'));
      navigate('/sas');
    } catch (error) {
      console.error('Microsoft Login Error:', error);
    }
  };

  return (
    (loading) ? (
      <div>
        <Box sx={{ py: 2,textAlign: 'center', mb: 2 }}>
            <Typography sx={{ variant: "h6", gutterBottom: true, pt: 8 }}>  </Typography>
            <img
                // src="/logo_banane_transparent.png"
                src="/animation_loading.gif"
                alt={t('login.logoAlt')}
                // className="login-logo-image rotate-animation" // Added animation class
                style={{ height: '50%' }} // Adjust size as needed
            />
            <Typography sx={{ variant: "h6", gutterBottom: true, pt: 8 }}>
                {t('login.processing')}
            </Typography>
        </Box>
      </div>
    ) : (
    <Box
      className="login-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5' // Optional: background color for the page
      }}
    >
      <Box
        className="login-form-wrapper"
        sx={{
          padding: { xs: 2, sm: 3, md: 4 },
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px'
        }}
      >
        <Box className="login-logo-container" sx={{ textAlign: 'center', mb: 2 }}>
          <img
            src="/logo_banane_transparent.png"
            alt={t('login.logoAlt')}
            className="login-logo-image"
            style={{ height: '60px' }} // Adjust size as needed
          />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', fontWeight: 'medium' }}>
          {t('login.loginTitle')}
        </Typography>
        <Box sx={{ display: 'flex', mx: 3 }}>
          <Button
            key="login-oidc-button"
            variant="contained"
            color="white"
            disabled={loading}
            onClick={() => handleMicrosoftLogin()}
            fullWidth
            sx={{ p: 2, mt: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', textTransform: 'none' }}
          >
            <img
              src="/ms-symbollockup_mssymbol_19.svg"
              alt="Microsoft Symbol"
              style={{ height: '20px', marginRight: '8px' }}
            />
            {t('login.initiateOidcFlowMicrosoft')}
          </Button>
        </Box>
        
        <Divider sx={{ p: 2 }}>{t('login.OR')}</Divider>
        {/* <Button
          key="login-oidc-button"
          variant="contained"
          color="white"
          onClick={() => loginGoogle()}
          fullWidth
          sx={{ p: 2, mt: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', textTransform: 'none' }}
        >
          <img
            src="/google-icon-logo.svg"
            alt="Google Symbol"
            style={{ height: '20px', marginRight: '8px' }}
          />
          {t('login.initiateOidcFlowGoogle')}
        </Button> */}

        <Box sx={{ mx: 3 }}>
          <GoogleLogin
            text={t('login.initiateOidcFlowGoogle')}
            disabled={loading}
            logo_alignment="center"
            onSuccess={async (credentialResponse) => {
              setLoading(true);
              try {
                await loginWithExternalToken(credentialResponse.credential);
                window.dispatchEvent(new Event('tokenChanged'));
                await fetchUserData(); // Ensure user data is fetched after token is set

                // Fetch tenants and redirect to the first one
                const tenants = await fetchTenantsFromApi();
                if (tenants && tenants.length > 0) {
                  navigate('/sas');
                }
              } catch (error) {
                console.error('Error logging in with external token:', error);
              }
              
              setLoading(false);
              return;

            }}
            onError={() => {
              setLoading(false);
              console.log('Login Failed');
            }}
          />
        </Box>
        <Divider sx={{ p: 2 }}>{t('login.OR')}</Divider>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleSubmit} className="login-form">
          <TextField
            label={t('login.emailLabel')}
            variant="outlined"
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('login.passwordLabel')}
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('login.togglePasswordVisibility')}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="login-button"
            disabled={loading}
            fullWidth
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? t('login.loggingIn') : t('login.signInButton')}
          </Button>
          
        </form>
        
      </Box>
    </Box>
  )
  );
};

export default Index;
